import { useCallback, useMemo, useState } from "react";
import AadhaarKpiSchoolsModal from "@/components/AadhaarKpiSchoolsModal";
import HeroStatusChip from "@/components/HeroStatusChip";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Clock,
  FileText,
  ShieldCheck,
  User,
} from "lucide-react";
import { BlockLink } from "@/components/DrilldownLink";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  buildAadhaarExecutiveMarkdown,
  buildBlockRiskReviewRows,
  buildCoverageLeaderboard,
  buildExecutiveActionQueueItems,
  buildExecutiveHeroNarrative,
  buildExecutiveTriageCards,
  buildPrimaryExecutiveAction,
  buildStatusDistributionView,
  buildStatusDonutStyle,
  fmt,
  formatCompact,
  pct,
} from "@/utils/aadhaarDerived";
import "./AadhaarExecutiveTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const TRIAGE_ICONS = {
  failed: AlertTriangle,
  mismatch: User,
  mbu: Clock,
  "not-provided": FileText,
};

const ACTION_ICONS = {
  red: AlertTriangle,
  purple: User,
  orange: Clock,
  "": FileText,
};

const DECISION_STACK = [
  {
    iconClass: "green",
    Icon: BarChart3,
    title: "District Officer",
    body: "Review coverage health, top exception contributors and pending ageing in one meeting-ready view.",
  },
  {
    iconClass: "orange",
    Icon: FileText,
    title: "Block Officer",
    body: "Own the block-wise exception queue, assign schools and track daily closure.",
  },
  {
    iconClass: "purple",
    Icon: User,
    title: "School Head / Class Teacher",
    body: "Resolve not-provided, mismatch and parent-confirmation items at the student level.",
  },
];

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="aa-exec-tooltip">
      <p>{label}</p>
      {payload.map((entry) => (
        <p key={entry.name}>
          {entry.name}: {typeof entry.value === "number" ? entry.value.toLocaleString("en-IN") : entry.value}
          {entry.unit || ""}
        </p>
      ))}
    </div>
  );
};

export default function AadhaarExecutiveTab({
  overview = {},
  blockData = [],
  statusDistribution = null,
  paretoData = [],
  onNavigateTab,
  scopeLabel = "Pune District",
}) {
  const [kpiMetricModal, setKpiMetricModal] = useState(null);
  const coverage = overview.aadhaar_coverage_pct ?? 0;
  const exceptionCount = Math.max(
    0,
    (overview.total_enrolment ?? 0) - (overview.aadhaar_passed ?? 0)
  );
  const mbuCount = overview.mbu_pending_total ?? 0;
  const hasData = Boolean(overview?.total_enrolment);

  const buildLocalMarkdown = useCallback(
    () => buildAadhaarExecutiveMarkdown(overview, blockData),
    [overview, blockData]
  );

  const scopeKey = useMemo(
    () => JSON.stringify({ c: overview?.aadhaar_coverage_pct, e: overview?.total_enrolment }),
    [overview?.aadhaar_coverage_pct, overview?.total_enrolment]
  );

  const { loading: aiLoading, pickFromBucket, pickPrimaryAction } = useMistralNarrativeOverlay({
    kind: "aadhaar-executive",
    title: "Aadhaar Executive Summary",
    buildLocalMarkdown,
    context: { overview, blockData },
    enabled: hasData,
    scopeKey,
  });

  const gaugeStyle = useMemo(
    () => ({
      background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--aa-success) 0 ${coverage}%, rgba(16,185,129,.14) ${coverage}% 100%)`,
    }),
    [coverage]
  );

  const localNarrative = useMemo(() => buildExecutiveHeroNarrative(overview), [overview]);
  const narrative = useMemo(
    () => (aiLoading ? localNarrative : pickFromBucket(localNarrative, "insights", 0)),
    [aiLoading, pickFromBucket, localNarrative]
  );
  const localPrimaryAction = useMemo(() => buildPrimaryExecutiveAction(blockData), [blockData]);
  const primaryAction = useMemo(
    () => (aiLoading ? localPrimaryAction : pickPrimaryAction(localPrimaryAction)),
    [aiLoading, pickPrimaryAction, localPrimaryAction]
  );
  const triageCards = useMemo(() => buildExecutiveTriageCards(overview), [overview]);
  const coverageBars = useMemo(() => buildCoverageLeaderboard(blockData, 8), [blockData]);
  const distribution = useMemo(
    () => buildStatusDistributionView(statusDistribution?.distribution || []),
    [statusDistribution]
  );
  const donutStyle = useMemo(
    () => buildStatusDonutStyle(statusDistribution?.distribution || []),
    [statusDistribution]
  );
  const actionQueue = useMemo(
    () => buildExecutiveActionQueueItems(blockData, paretoData),
    [blockData, paretoData]
  );
  const reviewRows = useMemo(() => buildBlockRiskReviewRows(blockData, 8), [blockData]);
  const paretoChartData = useMemo(() => paretoData.slice(0, 8), [paretoData]);

  const coverageChip =
    coverage >= 95 ? "Strong Coverage" : coverage >= 90 ? "Improving Coverage" : "Coverage Watch";
  const mbuChip = (overview.mbu_pending_total_pct ?? 0) >= 12 ? "MBU Follow-up" : "MBU Stable";

  const openKpiModal = (metric) => () => setKpiMetricModal(metric);
  const onKpiKeyDown = (metric) => (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setKpiMetricModal(metric);
    }
  };

  return (
    <div className="aa-exec-tab">
      <section className="aa-exec-hero" id="aa-exec-hero">
        <div className="aa-exec-hero-layout">
          <div>
            <div className="aa-exec-main">
              <div className="aa-exec-gauge" style={gaugeStyle} aria-label={`Aadhaar coverage ${pct(coverage)}`}>
                <div>
                  <strong>{pct(coverage)}</strong>
                  <span>Coverage</span>
                </div>
              </div>
              <div className="aa-exec-copy">
                <h2 className="dashboard-kpi-hero-title">Aadhaar Readiness Index</h2>
                <p>{narrative}</p>
                <div className="aa-exec-status-row">
                  <HeroStatusChip chipClass="aa-exec-status-chip" variant="green">
                    {coverageChip}
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="aa-exec-status-chip"
                    variant="amber"
                    onClick={openKpiModal("mbu_pending")}
                    ariaLabel="MBU pending — view schools with pending mobile updates"
                  >
                    {mbuChip}
                  </HeroStatusChip>
                  <HeroStatusChip chipClass="aa-exec-status-chip" variant="blue">
                    Live district data
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="aa-exec-summary-note">
              <div className="aa-exec-icon-box orange">
                <AlertTriangle size={20} />
              </div>
              <div>
                <b>Primary executive action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="aa-exec-metric-grid" aria-label="Executive Aadhaar KPIs">
            <article className="aa-exec-metric-card">
              <small>Total Students</small>
              <strong>{fmt(overview.total_enrolment)}</strong>
              <span>Selected academic year</span>
            </article>
            <article
              className="aa-exec-metric-card clickable"
              role="button"
              tabIndex={0}
              aria-label="Exception rate: view schools with open Aadhaar records"
              onClick={openKpiModal("exception_rate")}
              onKeyDown={onKpiKeyDown("exception_rate")}
            >
              <small>Exception Rate</small>
              <strong>{pct(overview.aadhaar_exception_pct)}</strong>
              <span className="bad">≈{formatCompact(exceptionCount)} records need attention</span>
            </article>
            <article
              className="aa-exec-metric-card clickable"
              role="button"
              tabIndex={0}
              aria-label="Name mismatch: view schools with identity verification gaps"
              onClick={openKpiModal("name_mismatch")}
              onKeyDown={onKpiKeyDown("name_mismatch")}
            >
              <small>Name Mismatch</small>
              <strong>{pct(overview.name_mismatch_pct)}</strong>
              <span className="warn">High-risk identity quality gap</span>
            </article>
            <article
              className="aa-exec-metric-card clickable"
              role="button"
              tabIndex={0}
              aria-label="MBU pending: view schools with biometric update backlog"
              onClick={openKpiModal("mbu_pending")}
              onKeyDown={onKpiKeyDown("mbu_pending")}
            >
              <small>MBU Pending</small>
              <strong>{pct(overview.mbu_pending_total_pct)}</strong>
              <span className="purple">≈{formatCompact(mbuCount)} pending updates</span>
            </article>
          </div>
        </div>
      </section>

      <div className="aa-exec-section-title" id="aa-exec-snapshot">
        <div>
          <h2>Coverage Snapshot</h2>
          <p>Status distribution and block-wise coverage leaderboard for quick scanning.</p>
        </div>
        <button type="button" className="aa-exec-section-action" onClick={() => onNavigateTab?.("operational")}>
          View diagnostics →
        </button>
      </div>

      <section className="aa-exec-snapshot-grid">
        <article className="aa-exec-panel">
          <div className="aa-exec-panel-head">
            <div>
              <h3>Aadhaar Status Distribution</h3>
              <p>Clear status mix with the key ratio shown in the centre.</p>
            </div>
            <span className="aa-exec-pill">Status mix</span>
          </div>
          <div className="aa-exec-donut-area">
            <div className="aa-exec-donut" style={{ background: donutStyle.background }} aria-hidden="true">
              <div className="aa-exec-donut-center">
                <div>
                  <strong>{pct(donutStyle.linkedPct ?? coverage, 1)}</strong>
                  <span>Linked</span>
                </div>
              </div>
            </div>
            <div className="aa-exec-legend">
              {distribution.map((item) => (
                <div key={item.name} className="aa-exec-legend-row">
                  <span>
                    <i className={`aa-exec-dot ${item.dotClass || ""}`} style={{ background: item.color }} />
                    {item.name}
                  </span>
                  <b>{pct(item.percentage, 1)}</b>
                </div>
              ))}
            </div>
          </div>
          <div className="aa-exec-insight-box">
            <b>Why this is better</b>
            <span>
              The main success number is visible immediately, while failed and not-provided records are
              grouped as action-focused exceptions.
            </span>
          </div>
        </article>

        <article className="aa-exec-panel">
          <div className="aa-exec-panel-head">
            <div>
              <h3>Block-Wise Aadhaar Coverage</h3>
              <p>Readable horizontal bars ranked by highest coverage blocks.</p>
            </div>
            <span className="aa-exec-pill">Top blocks</span>
          </div>
          <div className="aa-exec-coverage-bars">
            {coverageBars.map((row) => (
              <div key={row.block} className="aa-exec-bar-row">
                <div className="aa-exec-bar-label">
                  <BlockLink blockName={row.block}>{row.block}</BlockLink>
                </div>
                <div className="aa-exec-bar-track">
                  <div className="aa-exec-bar-fill" style={{ width: `${row.coverage}%` }} />
                </div>
                <div className="aa-exec-bar-value">{pct(row.coverage, 1)}</div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="aa-exec-section-title" id="aa-exec-triage">
        <div>
          <h2>Exception Triage</h2>
          <p>Four focused triage cards convert percentages into operational queues.</p>
        </div>
        <button type="button" className="aa-exec-section-action" onClick={() => onNavigateTab?.("action")}>
          Assign actions →
        </button>
      </div>

      <section className="aa-exec-triage-grid">
        {triageCards.map((card) => {
          const Icon = TRIAGE_ICONS[card.id] || ShieldCheck;
          return (
            <article key={card.id} className="aa-exec-triage-card">
              <div className={`aa-exec-icon-box ${card.iconClass}`}>
                <Icon size={20} />
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <strong>{card.value}</strong>
              <p>{card.body}</p>
              <div className="aa-exec-progress-line">
                <i className={card.barClass} style={{ width: `${card.barWidth}%` }} />
              </div>
            </article>
          );
        })}
      </section>

      <div className="aa-exec-section-title" id="aa-exec-diagnostics">
        <div>
          <h2>Exception Diagnostics</h2>
          <p>High-impact blocks with an accountable action queue beside the Pareto view.</p>
        </div>
        <button type="button" className="aa-exec-section-action" onClick={() => onNavigateTab?.("insights")}>
          Go to insights →
        </button>
      </div>

      <section className="aa-exec-analytics-grid">
        <article className="aa-exec-panel aa-exec-pareto-card">
          <div className="aa-exec-panel-head">
            <div>
              <h3>Exception Contribution by Block</h3>
              <p>Top contributors shown first with cumulative line for Pareto review.</p>
            </div>
            <span className="aa-exec-pill">Pareto</span>
          </div>
          <div className="aa-exec-pareto-chart">
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={paretoChartData} margin={{ left: 8, right: 16, bottom: 48 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="block_name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={70} />
                <YAxis yAxisId="left" orientation="left" />
                <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                <Tooltip content={<ChartTooltip />} />
                <Bar yAxisId="left" dataKey="exceptions" name="Exceptions" fill="#2563eb" radius={[8, 8, 0, 0]} />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="cumulative_pct"
                  name="Cumulative %"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="aa-exec-panel" id="aa-exec-actions">
          <div className="aa-exec-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Turn exceptions into owners, priorities and next steps.</p>
            </div>
            <span className="aa-exec-pill">Priority</span>
          </div>
          <div className="aa-exec-action-list">
            {actionQueue.map((item) => {
              const Icon = ACTION_ICONS[item.iconClass] || CheckCircle2;
              return (
                <div key={item.title} className="aa-exec-action-item">
                  <div className={`aa-exec-icon-box ${item.iconClass}`}>
                    <Icon size={20} />
                  </div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                  <span className={`aa-exec-priority ${item.priorityClass}`}>{item.priority}</span>
                </div>
              );
            })}
          </div>
        </article>
      </section>

      <div className="aa-exec-section-title" id="aa-exec-decisions">
        <div>
          <h2>Block Accountability and Decision Stack</h2>
          <p>Risk-ranked block review list with operating model for each stakeholder group.</p>
        </div>
        <button type="button" className="aa-exec-section-action" onClick={() => scrollToId("aa-exec-hero")}>
          Back to top ↑
        </button>
      </div>

      <section className="aa-exec-two-column">
        <article className="aa-exec-panel aa-exec-table-card">
          <div className="aa-exec-table-head">
            <div>
              <h3>Block-Wise Review List</h3>
              <p>Risk-ranked list combining coverage, exception rate and pending cases.</p>
            </div>
            <span className="aa-exec-pill">Review queue</span>
          </div>
          <div className="aa-exec-table-wrap">
            <table className="aa-exec-table" aria-label="Block-wise Aadhaar risk review">
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>Block</th>
                  <th>Coverage</th>
                  <th>Exception Rate</th>
                  <th>Name Mismatch</th>
                  <th>MBU Pending</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {reviewRows.length === 0 ? (
                  <tr>
                    <td colSpan={7}>No block data in current scope.</td>
                  </tr>
                ) : (
                  reviewRows.map((row) => (
                    <tr key={row.block}>
                      <td>{row.priority}</td>
                      <td>
                        <BlockLink blockName={row.block}>{row.block}</BlockLink>
                      </td>
                      <td className="aa-exec-score">{pct(row.coverage, 1)}</td>
                      <td>{pct(row.exceptionRate, 1)}</td>
                      <td>{pct(row.nameMismatch, 1)}</td>
                      <td>{pct(row.mbuPending, 1)}</td>
                      <td>
                        <span className={`aa-exec-tag ${row.statusTone}`}>{row.status}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="aa-exec-panel">
          <div className="aa-exec-panel-head">
            <div>
              <h3>Executive Decision Stack</h3>
              <p>What each user group should do from this tab.</p>
            </div>
            <span className="aa-exec-pill">Operating model</span>
          </div>
          <div className="aa-exec-decision-card">
            {DECISION_STACK.map((item) => (
              <div key={item.title} className="aa-exec-decision-item">
                <div className={`aa-exec-icon-box ${item.iconClass}`}>
                  <item.Icon size={20} />
                </div>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.body}</p>
                </div>
              </div>
            ))}
            <div className="aa-exec-action-footer">
              <b>Design outcome</b>
              <span>
                Health → snapshot → triage → diagnostics → accountability. Officers can move from
                dashboard viewing to measurable review actions in one flow.
              </span>
            </div>
          </div>
        </article>
      </section>

      <AadhaarKpiSchoolsModal
        open={Boolean(kpiMetricModal)}
        metric={kpiMetricModal}
        onClose={() => setKpiMetricModal(null)}
        scopeLabel={scopeLabel}
        districtPct={
          kpiMetricModal === "exception_rate"
            ? overview?.aadhaar_exception_pct
            : kpiMetricModal === "name_mismatch"
              ? overview?.name_mismatch_pct
              : kpiMetricModal === "mbu_pending"
                ? overview?.mbu_pending_total_pct
                : null
        }
        districtAggregate={
          kpiMetricModal === "exception_rate"
            ? exceptionCount
            : kpiMetricModal === "name_mismatch"
              ? Math.max(0, (overview?.aadhaar_passed ?? 0) - (overview?.name_match ?? 0))
              : kpiMetricModal === "mbu_pending"
                ? mbuCount
                : null
        }
      />
    </div>
  );
}
